import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useListings, useOwnerListings } from '../../../hooks/useListings';
import { LanguageCode, Listing, ShiurAudience, ShiurWhere } from '../../../types';
import { LANGUAGE_CODES, SHIUR_AUDIENCES } from '../../../config/categories';
import { pickLang } from '../../../utils/listingDisplay';
import { nextAudience } from '../../../utils/shiurForm';
import { BilingualField, ChipSelect, SelectField, TextField } from './formFields';

const WHERE_OPTIONS: ShiurWhere[] = ['shul', 'place', 'online'];

type Draft = Record<string, unknown>;

/**
 * The shuls this person may link a shiur to: the ones they own (the super
 * admin: every shul), plus the one already linked so it stays visible.
 */
export function useLinkableShuls(currentShulId?: string): { shuls: Listing[]; loading: boolean } {
  const { user } = useAuth();
  const isSuper = user?.role === 'superAdmin';
  const own = useOwnerListings(isSuper ? undefined : user?.uid);
  const all = useListings({ category: 'shul', includeUnpublished: true, skip: !isSuper });
  const { listings: current } = useListings({ category: 'shul', skip: !currentShulId || isSuper });
  const { i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const shuls = useMemo(() => {
    const base = (isSuper ? all.listings : own.listings).filter((l) => l.category === 'shul' && l.status !== 'deleted');
    const linked = current.find((l) => l.id === currentShulId);
    const merged = linked && !base.some((l) => l.id === linked.id) ? [...base, linked] : base;
    return merged.sort((a, b) => pickLang(a.nameEn, a.nameHe, isHebrew).localeCompare(pickLang(b.nameEn, b.nameHe, isHebrew)));
  }, [isSuper, all.listings, own.listings, current, currentShulId, isHebrew]);
  return { shuls, loading: isSuper ? all.loading : own.loading };
}

/** Shiur details (docs/shiurim-plan.md): who gives it, topic text, audience, languages, where. */
export default function ShiurDetailsFields({
  listing,
  draft,
  update,
  showShulError,
}: {
  listing: Extract<Listing, { category: 'shiur' }>;
  draft: Draft;
  update: (patch: Draft) => void;
  showShulError: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const s = (key: string) => t(`directory.shiur.${key}`);
  const str = (key: string) => (draft[key] as string | undefined) ?? '';
  const where = (draft.where as ShiurWhere | undefined) ?? 'place';
  const { shuls, loading } = useLinkableShuls(listing.details?.shulId);

  return (
    <>
      <BilingualField
        labelHe={s('giverHe')}
        labelEn={s('giverEn')}
        valueHe={str('giverHe')}
        valueEn={str('giverEn')}
        onChangeHe={(v) => update({ giverHe: v })}
        onChangeEn={(v) => update({ giverEn: v })}
      />

      {listing.subcategory === 'other' && (
        <>
          <BilingualField
            labelHe={s('topicOtherHe')}
            labelEn={s('topicOtherEn')}
            valueHe={str('topicOtherHe')}
            valueEn={str('topicOtherEn')}
            onChangeHe={(v) => update({ topicOtherHe: v })}
            onChangeEn={(v) => update({ topicOtherEn: v })}
            maxLength={100}
          />
          <p className="text-xs text-ash -mt-3">{s('topicOtherHint')}</p>
        </>
      )}

      <ChipSelect<ShiurAudience>
        label={s('audience')}
        info={s('audienceHint')}
        options={SHIUR_AUDIENCES.map((a) => ({ value: a, label: t(`directory.audience.${a}`) }))}
        selected={(draft.audience as ShiurAudience[] | undefined) ?? []}
        onChange={(next) => update({ audience: nextAudience((draft.audience as ShiurAudience[] | undefined) ?? [], next) })}
      />

      <ChipSelect<LanguageCode>
        label={s('languages')}
        options={LANGUAGE_CODES.map((c) => ({ value: c, label: t(`directory.language.${c}`) }))}
        selected={(draft.languages as LanguageCode[] | undefined) ?? []}
        onChange={(next) => update({ languages: next })}
      />

      <SelectField<ShiurWhere>
        label={s('where')}
        value={where}
        onChange={(v) => update({ where: v })}
        options={WHERE_OPTIONS.map((w) => ({ value: w, label: t(`directory.shiur.whereOption.${w}`) }))}
      />

      {where === 'shul' && (
        <div className="space-y-3 rounded-[16px] border border-ink/10 p-3">
          {!loading && shuls.length === 0 ? (
            <p className="text-sm text-ink">{s('noShuls')}</p>
          ) : (
            <SelectField
              label={s('shul')}
              info={s('shulHint')}
              value={str('shulId')}
              onChange={(v) => update({ shulId: v })}
              options={[
                { value: '', label: s('pickShul') },
                ...shuls.map((l) => ({ value: l.id, label: pickLang(l.nameEn, l.nameHe, isHebrew) })),
              ]}
            />
          )}
          {showShulError && !str('shulId') && <p className="text-sm text-danger-600">{s('shulRequired')}</p>}
          <BilingualField
            labelHe={s('roomHe')}
            labelEn={s('roomEn')}
            valueHe={str('roomHe')}
            valueEn={str('roomEn')}
            onChangeHe={(v) => update({ roomHe: v })}
            onChangeEn={(v) => update({ roomEn: v })}
          />
        </div>
      )}

      <TextField
        label={s('onlineUrl')}
        hint={s('onlineUrlHint')}
        type="url"
        dir="ltr"
        value={str('onlineUrl')}
        onChange={(v) => update({ onlineUrl: v })}
        placeholder="https://"
      />
    </>
  );
}
